/**
 * The message engine: one implementation, used by the browser catalogue and the server one.
 *
 * A message is either a string or a set of plural forms. It may contain:
 *
 *   {name}            a named placeholder, replaced by `params.name`
 *   <tag>…</tag>      a slot the caller renders — bold, a link, a brand name in mono
 *
 * Both survive translation because the sentence is never split: the Italian may put `<b>` at
 * the front and `{count}` at the back and nothing in the code has to know.
 *
 * `formatMessage` renders to a plain string (server output, `aria-label`, `title`).
 * `resolveMessage` + `parseMessage` give the React renderer the same nodes to build elements
 * from, so a sentence reads identically whether it lands in a `<p>` or in a log line.
 */

export type PluralCategory = Intl.LDMLPluralRule

/**
 * A plural message. `other` is mandatory — it is the fallback for every category a locale
 * does not distinguish. `zero` is an *exact* form for `count === 0` (English and Italian have
 * no `zero` category, so `Intl` would never select it), useful for "No posts yet".
 */
export interface PluralMessage {
  zero?: string
  one?: string
  two?: string
  few?: string
  many?: string
  other: string
}

export type Message = string | PluralMessage

export type ParamValue = string | number

export type MessageParams = Readonly<Record<string, ParamValue>>

/* ── catalogue shapes ─────────────────────────────────────────────────────────
   These derive a catalogue's key union from the catalogue object itself, so a key that
   does not exist is a compile error rather than a blank on screen. */

/** Every key of `T`, as a string union. */
export type KeyOf<T> = keyof T & string

/** Keys whose message carries plural forms — `count` is mandatory on those. */
export type PluralKeyOf<T> = {
  [K in KeyOf<T>]: T[K] extends string ? never : K
}[KeyOf<T>]

/** Keys whose message is a single string. */
export type SimpleKeyOf<T> = Exclude<KeyOf<T>, PluralKeyOf<T>>

/**
 * Fails to compile unless `T` is empty. Used to assert that two catalogues carry the same
 * keys: the error names the offending key, which is the whole point.
 */
export type AssertNever<T extends never> = T

/** Keys present in `A` and missing from `B`. */
export type MissingFrom<A, B> = Exclude<KeyOf<A>, KeyOf<B>>

/** Keys whose message is plural in one catalogue and a plain string in the other. */
export type ShapeMismatch<A, B> = {
  [K in KeyOf<A> & KeyOf<B>]: A[K] extends string
    ? B[K] extends string
      ? never
      : K
    : B[K] extends string
      ? K
      : never
}[KeyOf<A> & KeyOf<B>]

/* ── plural selection ─────────────────────────────────────────────────────── */

const pluralRulesCache = new Map<string, Intl.PluralRules>()

const pluralRules = (intlLocale: string): Intl.PluralRules => {
  const cached = pluralRulesCache.get(intlLocale)
  if (cached) return cached
  const rules = new Intl.PluralRules(intlLocale)
  pluralRulesCache.set(intlLocale, rules)
  return rules
}

/**
 * Picks the form for `count`. English and Italian both distinguish `one` / `other`, and they
 * disagree at zero — English says *0 posts*, Italian says *0 post*. That difference lives in
 * the catalogue, not here.
 */
export const selectPluralForm = (
  message: PluralMessage,
  intlLocale: string,
  count: number | undefined,
): string => {
  if (count === undefined) return message.other
  if (count === 0 && message.zero !== undefined) return message.zero
  const category: PluralCategory = pluralRules(intlLocale).select(count)
  return message[category] ?? message.other
}

/** The single string a message resolves to, before placeholders are filled. */
export const resolveMessage = (
  message: Message,
  intlLocale: string,
  params?: MessageParams,
): string => {
  if (typeof message === 'string') return message
  const count = params === undefined ? undefined : params['count']
  return selectPluralForm(message, intlLocale, typeof count === 'number' ? count : undefined)
}

/* ── parsing ──────────────────────────────────────────────────────────────── */

export type MessageNode =
  | { readonly type: 'text'; readonly value: string }
  | { readonly type: 'param'; readonly name: string }
  | { readonly type: 'tag'; readonly name: string; readonly children: readonly MessageNode[] }

const NAME = '[A-Za-z][A-Za-z0-9_]*'
const PARAM_AT = new RegExp(`^\\{(${NAME})\\}`)
const OPEN_AT = new RegExp(`^<(${NAME})>`)
const CLOSE_AT = new RegExp(`^</(${NAME})>`)

interface ParseResult {
  nodes: MessageNode[]
  /** Index just past the last character consumed. */
  next: number
}

const parseNodes = (template: string, from: number, closing: string | null): ParseResult => {
  const nodes: MessageNode[] = []
  let text = ''
  let index = from

  const flush = (): void => {
    if (text.length > 0) {
      nodes.push({ type: 'text', value: text })
      text = ''
    }
  }

  while (index < template.length) {
    const rest = template.slice(index)

    const close = CLOSE_AT.exec(rest)
    if (close && close[1] !== undefined) {
      if (close[1] === closing) {
        flush()
        return { nodes, next: index + close[0].length }
      }
      // A stray closing tag is copy, not markup.
      text += close[0]
      index += close[0].length
      continue
    }

    const open = OPEN_AT.exec(rest)
    if (open && open[1] !== undefined) {
      const name = open[1]
      const inner = parseNodes(template, index + open[0].length, name)
      // An unclosed tag reaches the end of the template: treat the whole thing as text so a
      // translator's stray `<` never eats the rest of the sentence.
      if (inner.next > template.length) {
        text += open[0]
        index += open[0].length
        continue
      }
      flush()
      nodes.push({ type: 'tag', name, children: inner.nodes })
      index = inner.next
      continue
    }

    const param = PARAM_AT.exec(rest)
    if (param && param[1] !== undefined) {
      flush()
      nodes.push({ type: 'param', name: param[1] })
      index += param[0].length
      continue
    }

    text += template.charAt(index)
    index += 1
  }

  flush()
  // Ran out of template while inside a tag: signal it with an out-of-range cursor.
  return { nodes, next: closing === null ? index : template.length + 1 }
}

const parseCache = new Map<string, readonly MessageNode[]>()

/** Tokenises a resolved message. Results are cached — templates are a closed set. */
export const parseMessage = (template: string): readonly MessageNode[] => {
  const cached = parseCache.get(template)
  if (cached) return cached
  const parsed = parseNodes(template, 0, null).nodes
  parseCache.set(template, parsed)
  return parsed
}

/* ── parameter formatting ─────────────────────────────────────────────────── */

const numberFormatCache = new Map<string, Intl.NumberFormat>()

const numberFormat = (intlLocale: string): Intl.NumberFormat => {
  const cached = numberFormatCache.get(intlLocale)
  if (cached) return cached
  const formatter = new Intl.NumberFormat(intlLocale)
  numberFormatCache.set(intlLocale, formatter)
  return formatter
}

/**
 * Numbers interpolated into a sentence are formatted in the active locale — `1.234` in
 * Italian, `1,234` in English. A caller that wants another form passes a string it formatted
 * itself through `format.ts`.
 */
export const formatParam = (value: ParamValue, intlLocale: string): string =>
  typeof value === 'number' ? numberFormat(intlLocale).format(value) : value

/* ── rendering ────────────────────────────────────────────────────────────── */

const renderNodes = (
  nodes: readonly MessageNode[],
  intlLocale: string,
  params: MessageParams | undefined,
): string => {
  let out = ''
  for (const node of nodes) {
    if (node.type === 'text') {
      out += node.value
      continue
    }
    if (node.type === 'tag') {
      // A string render drops the markers and keeps the sentence.
      out += renderNodes(node.children, intlLocale, params)
      continue
    }
    const value = params?.[node.name]
    // An unknown placeholder stays visible as `{name}`: silent blanks hide themselves in QA.
    out += value === undefined ? `{${node.name}}` : formatParam(value, intlLocale)
  }
  return out
}

/** Message → finished string. The only string renderer in the repo. */
export const formatMessage = (
  message: Message,
  intlLocale: string,
  params?: MessageParams,
): string => renderNodes(parseMessage(resolveMessage(message, intlLocale, params)), intlLocale, params)

/* ── authoring checks (used by the parity script and by tests) ────────────── */

export interface MessagePlaceholders {
  params: readonly string[]
  tags: readonly string[]
}

const collect = (
  nodes: readonly MessageNode[],
  params: Set<string>,
  tags: Set<string>,
): void => {
  for (const node of nodes) {
    if (node.type === 'param') params.add(node.name)
    else if (node.type === 'tag') {
      tags.add(node.name)
      collect(node.children, params, tags)
    }
  }
}

/** Every `{placeholder}` and `<tag>` a template uses, sorted. */
export const placeholdersOf = (template: string): MessagePlaceholders => {
  const params = new Set<string>()
  const tags = new Set<string>()
  collect(parseMessage(template), params, tags)
  return { params: [...params].sort(), tags: [...tags].sort() }
}

/** Every form a message carries: `['other']` for a plain string. */
export const formsOf = (message: Message): readonly string[] =>
  typeof message === 'string' ? ['other'] : Object.keys(message).sort()
